import { useBlockEditorContext } from "..";
import type { Editor } from "@tiptap/react";
import { t } from "@/i18n-js/instance";
import { classNames } from "@/react/helpers/twMergeWithCN";
import { usePunditUserContext } from "@circle-react/contexts";
import { ProgressiveImage } from "@circle-react-shared/ProgressiveImage";
import { supportsGalleryPreview } from "@circle-react-uikit/TipTap/utilities/contentTypeParser";
import { AlignmentPicker } from "./AlignmentPicker";
import { EmailImageToolbar } from "./EmailImage/EmailImageToolbar";

interface ImageContainerProps {
  attachmentMeta: any;
  deleteNode: () => void;
  editor: Editor;
  node: any;
  onClickOpenAttachmentGallery: () => void;
  updateAttributes: (attrs: any) => void;
}

export const ImageContainer = ({
  node,
  editor,
  attachmentMeta,
  onClickOpenAttachmentGallery,
  updateAttributes,
  deleteNode,
}: ImageContainerProps) => {
  const { currentCommunitySettings } = usePunditUserContext();
  const { isWithinEmailEditor } = useBlockEditorContext() ?? {};
  const isProgressiveImageLoadingOnPostsEnabled =
    currentCommunitySettings?.progressive_image_loading_on_post_space_enabled;
  const isEditable = editor.isEditable;
  const isPreviewable = supportsGalleryPreview(attachmentMeta.content_type);

  const shouldShowNormalToolbar = isEditable && !isWithinEmailEditor;
  /* Older emails would have images in old format. Show these images, but with the email toolbar. */
  const shouldShowEmailToolbar = isEditable && isWithinEmailEditor;

  let widthRatio = 1;
  if (node.attrs.width_ratio) {
    widthRatio = node.attrs.width_ratio;
  } else if (node.attrs.width) {
    // Temporary failsafe in case data is not properly migrated.
    widthRatio = node.attrs.width === "100%" ? 1 : 0.5;
  }

  const width = `${widthRatio * 100}%`;

  const originalWidth = attachmentMeta?.metadata?.width;
  const originalHeight = attachmentMeta?.metadata?.height;
  const aspectRatio =
    originalWidth && originalHeight
      ? `${originalWidth} / ${originalHeight}`
      : undefined;

  return (
    <div
      style={{ width }}
      className={classNames("group relative", {
        "mx-auto": node.attrs.alignment === "center",
      })}
    >
      <button
        type="button"
        className="w-full"
        onClick={onClickOpenAttachmentGallery}
        aria-label={t("tiptap.extensions.image_preview.view_attachment")}
        disabled={!isEditable && !isPreviewable}
      >
        {isProgressiveImageLoadingOnPostsEnabled ? (
          <ProgressiveImage
            alt=""
            src={attachmentMeta.url}
            className={classNames("w-full", {
              "cursor-zoom-in": !isEditable && isPreviewable,
            })}
            srcVariants={attachmentMeta?.image_variants}
            aspectRatio={aspectRatio}
          />
        ) : (
          <img
            loading="lazy"
            alt=""
            className={classNames("w-full", {
              "cursor-zoom-in": !isEditable && isPreviewable,
            })}
            src={attachmentMeta.url}
          />
        )}
      </button>
      {shouldShowNormalToolbar && (
        <AlignmentPicker node={node} updateAttributes={updateAttributes} />
      )}
      {shouldShowEmailToolbar && (
        <EmailImageToolbar
          editor={editor}
          node={node}
          updateAttributes={updateAttributes}
          deleteNode={deleteNode}
        />
      )}
    </div>
  );
};
